// src/modules/operaciones/inventarios/utils/analizarDatos.js
//
// ORQUESTADOR del análisis de informes.
//
//   datos (parseado del backend) + filtros (frontend)
//        ├─ detectarRoles(datos.columnas)
//        ├─ analizarMermas(datos, roles, filtros)
//        │     └─ esMermas: true  → dashboard completo
//        └─ esMermas: false         → análisis genérico (el de siempre: Top 5 barras + torta)
//
// COMPATIBILIDAD: el resultado SIEMPRE trae las claves que ya usaba el sistema:
//   meta, columnas, totalFilas, totalColumnas, insights (string[]),
//   graficoBarras ([{ name, value }] | null), graficoTorta ([{ name, value }] | null),
//   colsNumericas, colsTexto.
//
// Modo "mermas": además trae todo lo de analizarMermas (kpis, puente, bodegas, fabricantes,
//   familias, articulos, largo, validaciones, alertas, catalogos, datos) y:
//     - `meta`          = metadatos del ARCHIVO (nombreArchivo...), igual que antes.
//     - `metaAnalisis`  = el `meta` que devuelve analizarMermas (totalArticulos, filtrosActivos...).
//     - graficoBarras   = Top 5 fabricantes por venta; graficoTorta = familias por venta.
// Modo "generico": además trae `modo: "generico"` y `esMermas: false`.
//
// USO
//   analizarDatos(datos)                              // como antes
//   analizarDatos(datos, filtros)                     // con filtros del dashboard
//   analizarDatos(datos, filtros, { mapeoManual })    // con corrección manual de columnas

import { detectarRoles, aNumero, normalizarTexto, separarFilasTotal } from "./detectorColumnas";
import { analizarMermas, OPCIONES_POR_DEFECTO } from "./analizarMermas";
import { fmtCompacto, fmtNumero, fmtPorcentaje } from "./informesComun";

const claveMayus = (v) => normalizarTexto(v, { mayusculas: true });

/* ========================================================================== */
/* Modo MERMAS                                                                */
/* ========================================================================== */

// Solo pasa a analizarMermas las opciones que ese módulo conoce.
function opcionesParaMermas(filtros) {
  const salida = {};
  if (!filtros || typeof filtros !== "object") return salida;
  Object.keys(OPCIONES_POR_DEFECTO).forEach((k) => {
    if (filtros[k] !== undefined && filtros[k] !== null) salida[k] = filtros[k];
  });
  return salida;
}

function insightsMermas(datos, r) {
  const k = r.kpis;
  const out = [];
  const nombre = datos.meta?.nombreArchivo ? `El archivo "${datos.meta.nombreArchivo}"` : "El archivo";

  out.push(
    `${nombre} es un informe de mermas por bodega: ${fmtNumero(k.articulos.total)} artículos` +
      ` (${fmtNumero(k.articulos.conVenta)} con venta) y venta total de ${fmtCompacto(k.ventaTotal)}.`
  );
  if (k.mermaVentaPct !== null && k.mermaVentaPct !== undefined) {
    out.push(`La merma total neta es ${fmtCompacto(k.mermaTotalNeta)} (${fmtPorcentaje(k.mermaVentaPct)} de la venta).`);
  }
  if (k.perdidaBruta < 0) {
    out.push(
      `La pérdida bruta es ${fmtCompacto(k.perdidaBruta)}` +
        (k.perdidaBrutaPct !== null ? ` (${fmtPorcentaje(k.perdidaBrutaPct)} de la venta)` : "") +
        `; los sobrantes de ${fmtCompacto(k.sobrantes)} compensan parte de ella.`
    );
  }
  const peorBodega = r.bodegas?.ranking?.[0];
  if (peorBodega && peorBodega.neta < 0) {
    out.push(`La bodega con mayor merma neta es ${peorBodega.etiqueta} (${fmtCompacto(peorBodega.neta)}).`);
  }
  const peorFab = r.fabricantes?.topMerma?.[0];
  if (peorFab) out.push(`El fabricante con mayor merma neta es ${peorFab.nombre} (${fmtCompacto(peorFab.neta)}).`);

  const par = r.articulos?.pareto;
  if (par && par.articulos > 0) {
    out.push(`${fmtNumero(par.articulosPara80)} de ${fmtNumero(par.articulos)} artículos con pérdida concentran el 80 % de la pérdida bruta.`);
  }
  const errores = (r.alertas || []).filter((a) => a.nivel === "error").length;
  const avisos = (r.alertas || []).filter((a) => a.nivel === "advertencia").length;
  if (errores > 0) out.push(`Hay ${errores} error(es) de validación: revisa la pestaña Alertas.`);
  else if (avisos > 0) out.push(`Hay ${avisos} advertencia(s) de calidad de datos en la pestaña Alertas.`);
  return out;
}

/* ========================================================================== */
/* Modo GENÉRICO (el análisis original, ahora con filtros y sin filas total)  */
/* ========================================================================== */

// Filtros opcionales: bodegas, fabricantes y familias (solo si existen esas columnas).
function filtrarGenerico(filas, roles, filtros) {
  const g = roles.genericos || {};
  const reglas = [
    { lista: filtros?.bodegas, id: g.bodega },
    { lista: filtros?.fabricantes, id: roles.fabricanteCol || g.marca || g.proveedor },
    { lista: filtros?.familias, id: g.categoria },
  ]
    .filter((r) => Array.isArray(r.lista) && r.lista.length > 0 && r.id)
    .map((r) => ({ id: r.id, permitidos: new Set(r.lista.map(claveMayus)) }));
  if (reglas.length === 0) return filas;
  return filas.filter((f) => reglas.every((r) => r.permitidos.has(claveMayus(f[r.id]))));
}

function analisisGenerico(datos, roles, filtros) {
  const { columnas, meta } = datos;
  // Sin filas "Total general" (si no, las sumas se duplican) y con los filtros aplicados
  const filas = filtrarGenerico(separarFilasTotal(datos.filas, roles, datos.filaTotal).filas, roles, filtros);
  const hayFiltro = filas.length !== datos.filas.length;

  const totalFilas = hayFiltro ? filas.length : datos.filasTotales || filas.length;
  const totalColumnas = columnas.length;

  // 1. Identificar columnas por tipo ("codigo" no entra: no se suman los ITEM)
  const colsNumericas = columnas.filter((c) => c.tipo === "numero" || c.tipo === "moneda").map((c) => c.id);
  const colsTexto = columnas.filter((c) => c.tipo === "texto").map((c) => c.id);

  // 2. Insights
  const insights = [];
  insights.push(`El archivo "${meta?.nombreArchivo ?? ""}" contiene ${totalFilas} registros y ${totalColumnas} columnas analizadas.`);
  if (colsNumericas.length > 0) {
    insights.push(`Se encontraron ${colsNumericas.length} columnas numéricas, ideales para gráficos de barras o líneas.`);
  }
  if (colsTexto.length > 0) {
    insights.push(`Se detectaron ${colsTexto.length} columnas de texto, útiles para agrupaciones y gráficos de torta.`);
  }

  // 3. Gráfico A: Top 5 de la primera columna numérica agrupado por la primera columna de texto
  let graficoBarras = null;
  if (colsNumericas.length > 0 && colsTexto.length > 0) {
    const colNum = colsNumericas[0];
    const colText = colsTexto[0];
    const agrupado = {};
    filas.forEach((fila) => {
      const clave = fila[colText] || "Sin nombre";
      agrupado[clave] = (agrupado[clave] || 0) + aNumero(fila[colNum]);
    });
    graficoBarras = Object.entries(agrupado)
      .map(([name, value]) => ({ name, value }))
      .sort((a, b) => b.value - a.value)
      .slice(0, 5);

    const nombreColText = columnas.find((c) => c.id === colText)?.nombre || "Categoría";
    const nombreColNum = columnas.find((c) => c.id === colNum)?.nombre || "Valor";
    insights.push(`El gráfico de barras muestra el Top 5 de "${nombreColText}" con mayor "${nombreColNum}".`);
  }

  // Gráfico B: distribución (conteo) de la primera columna de texto
  let graficoTorta = null;
  if (colsTexto.length > 0) {
    const colText = colsTexto[0];
    const conteo = {};
    filas.forEach((fila) => {
      const clave = fila[colText] || "Vacío";
      conteo[clave] = (conteo[clave] || 0) + 1;
    });
    graficoTorta = Object.entries(conteo)
      .map(([name, value]) => ({ name, value }))
      .sort((a, b) => b.value - a.value)
      .slice(0, 5);

    const nombreColText = columnas.find((c) => c.id === colText)?.nombre || "Categoría";
    insights.push(`La gráfica circular muestra la distribución de los registros según "${nombreColText}".`);
  }

  return {
    modo: "generico",
    esMermas: false,
    roles,
    meta,
    columnas,
    totalFilas,
    totalColumnas,
    insights,
    graficoBarras,
    graficoTorta,
    colsNumericas,
    colsTexto,
  };
}

/* ========================================================================== */
/* Orquestador                                                                */
/* ========================================================================== */

export const analizarDatos = (datos, filtros = {}, { mapeoManual = null } = {}) => {
  if (!datos || !datos.columnas || !datos.filas) return null;

  const roles = detectarRoles(datos.columnas, { mapeoManual: mapeoManual || filtros?.mapeoManual || null });

  // 1) Intenta el dashboard de mermas (si falla, no se rompe: se usa el genérico)
  let mermas = null;
  try {
    mermas = analizarMermas(datos, roles, opcionesParaMermas(filtros));
  } catch (error) {
    console.error("analizarMermas falló; se usa el análisis genérico:", error);
  }

  if (mermas?.esMermas) {
    const colsNumericas = datos.columnas.filter((c) => c.tipo === "numero" || c.tipo === "moneda").map((c) => c.id);
    const colsTexto = datos.columnas.filter((c) => c.tipo === "texto").map((c) => c.id);

    return {
      ...mermas, // kpis, puente, bodegas, fabricantes, familias, articulos, largo, alertas, catalogos, datos...
      modo: "mermas",
      esMermas: true,
      roles,
      // --- claves heredadas ---
      meta: datos.meta,
      metaAnalisis: mermas.meta,
      columnas: datos.columnas,
      totalFilas: datos.filasTotales || datos.filas.length,
      totalColumnas: datos.columnas.length,
      insights: insightsMermas(datos, mermas),
      graficoBarras: (mermas.fabricantes?.topVenta || []).slice(0, 5).map((f) => ({ name: f.nombre, value: f.venta })),
      graficoTorta: (mermas.familias?.dona || []).map((f) => ({ name: f.name, value: f.value })),
      colsNumericas,
      colsTexto,
    };
  }

  // 2) Cualquier otro archivo: el análisis de siempre
  return analisisGenerico(datos, roles, filtros);
};

export default analizarDatos;